import React, { useState, useEffect } from 'react';
import { blogApi } from '../../../services/api';
import './BlogGrid.css';

const BlogGrid = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBlog, setSelectedBlog] = useState(null);

    useEffect(() => {
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await blogApi.getAll();
            setBlogs(data.blogs || []);
        } catch (err) {
            console.error('Error fetching blogs:', err);
            setError('Failed to load blogs. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    // Strip HTML tags for excerpt preview
    const stripHtml = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const truncateContent = (content, maxLength = 150) => {
        const plainText = stripHtml(content);
        if (plainText.length <= maxLength) return plainText;
        return plainText.substring(0, maxLength).trim() + '...';
    };

    const openBlogModal = (blog) => {
        setSelectedBlog(blog);
        document.body.style.overflow = 'hidden';
    };

    const closeBlogModal = () => {
        setSelectedBlog(null);
        document.body.style.overflow = 'auto';
    };

    if (loading) {
        return (
            <div className="blog-grid">
                <div className="blog-grid__container">
                    <div className="blog-grid__loading">
                        <div className="loading-spinner"></div>
                        <p>Loading blogs...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="blog-grid">
                <div className="blog-grid__container">
                    <div className="blog-grid__error">
                        <p>{error}</p>
                        <button onClick={fetchBlogs} className="retry-button">
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (blogs.length === 0) {
        return (
            <div className="blog-grid">
                <div className="blog-grid__container">
                    <div className="blog-grid__empty">
                        <h2>No blogs yet</h2>
                        <p>Check back soon for updates and insights from our studio.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-grid">
            <div className="blog-grid__container">
                <div className="blog-grid__header">
                    <h1>Latest Articles</h1>
                    <p>Explore our thoughts on architecture, design, and more</p>
                </div>

                <div className="blog-grid__cards">
                    {blogs.map((blog) => (
                        <article
                            key={blog._id}
                            className="blog-card"
                            onClick={() => openBlogModal(blog)}
                        >
                            <div className="blog-card__image">
                                {blog.pictures && blog.pictures.length > 0 ? (
                                    <img
                                        src={blog.pictures[0]}
                                        alt={blog.title}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.classList.add('no-image');
                                        }}
                                    />
                                ) : (
                                    <div className="blog-card__placeholder">
                                        <span>No Image</span>
                                    </div>
                                )}
                            </div>
                            <div className="blog-card__content">
                                <span className="blog-card__date">
                                    {formatDate(blog.createdAt)}
                                </span>
                                <h2 className="blog-card__title">{blog.title}</h2>
                                <p className="blog-card__excerpt">
                                    {truncateContent(blog.content)}
                                </p>
                                <span className="blog-card__read-more">Read More</span>
                            </div>
                        </article>
                    ))}
                </div>
            </div>

            {/* Blog Detail Modal */}
            {selectedBlog && (
                <div className="blog-modal__overlay" onClick={closeBlogModal}>
                    <div className="blog-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="blog-modal__close" onClick={closeBlogModal}>
                            &times;
                        </button>

                        <div className="blog-modal__content">
                            {selectedBlog.pictures && selectedBlog.pictures.length > 0 && (
                                <div className="blog-modal__images">
                                    {selectedBlog.pictures.map((pic, index) => (
                                        <img
                                            key={index}
                                            src={pic}
                                            alt={`${selectedBlog.title} - ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            )}

                            <div className="blog-modal__text">
                                <span className="blog-modal__date">
                                    {formatDate(selectedBlog.createdAt)}
                                </span>
                                <h1 className="blog-modal__title">{selectedBlog.title}</h1>
                                <div
                                    className="blog-modal__body"
                                    dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogGrid;
